import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pin, Repeat, SlidersHorizontal, Check } from "lucide-react";
import {
  MODE_META,
  SU_PLATFORM_META,
  SoftwareUpdateWizardState,
  SuMode,
  SuPlatform,
} from "../types";

const MODE_CARDS: Array<{
  id: SuMode;
  title: string;
  subtitle: string;
  icon: typeof Pin;
}> = [
  {
    id: "pinned",
    title: "Pinned version",
    subtitle: "Get everyone to a specific version by a deadline.",
    icon: Pin,
  },
  {
    id: "rolling",
    title: "Rolling (latest approved)",
    subtitle: "Always stay on the latest (or N-1), N days after release.",
    icon: Repeat,
  },
  {
    id: "update_behavior",
    title: "Update behavior",
    subtitle: "Control deferrals, automatic installs, and security updates.",
    icon: SlidersHorizontal,
  },
];

const PLATFORMS: SuPlatform[] = ["macos", "ios", "ipados", "tvos", "visionos"];

interface Props {
  state: SoftwareUpdateWizardState;
  onChange: (patch: Partial<SoftwareUpdateWizardState>) => void;
}

export function ModePickerStep({ state, onChange }: Props) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Pick a patching mode</CardTitle>
          <CardDescription>
            Each mode builds a different Apple declaration. You can change this later, but
            switching modes resets the configuration.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3">
          {MODE_CARDS.map((card) => {
            const Icon = card.icon;
            const active = state.mode === card.id;
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => onChange({ mode: card.id })}
                aria-pressed={active}
                className={`group relative text-left rounded-lg border p-4 transition-colors ${
                  active
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border hover:bg-muted/40"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  {active && (
                    <Check className="h-4 w-4 text-primary" aria-label="Selected" />
                  )}
                </div>
                <div className="mt-3 text-sm font-medium">{card.title}</div>
                <div className="mt-1 text-xs text-muted-foreground">{card.subtitle}</div>
                <div className="mt-3 text-[10px] uppercase tracking-wide text-muted-foreground/80">
                  {MODE_META[card.id].kind}
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Identify the policy</CardTitle>
          <CardDescription>Name, scope to a single target platform, and an optional description.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-2">
            <Label htmlFor="su-name">
              Policy name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="su-name"
              value={state.name}
              maxLength={120}
              placeholder={
                state.mode === "pinned"
                  ? "e.g. macOS patch to 15.5 by Jun 20"
                  : state.mode === "rolling"
                    ? "e.g. iPhone fleet — latest iOS, 7d after release"
                    : "e.g. Mac update behavior — knowledge workers"
              }
              onChange={(e) => onChange({ name: e.target.value })}
            />
          </div>

          <div className="grid gap-2 sm:max-w-xs">
            <Label htmlFor="su-platform">Target platform</Label>
            <Select
              value={state.platform}
              onValueChange={(v) => onChange({ platform: v as SuPlatform })}
            >
              <SelectTrigger id="su-platform">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {SU_PLATFORM_META[p].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="su-desc">Description</Label>
            <Textarea
              id="su-desc"
              rows={3}
              value={state.description}
              maxLength={500}
              placeholder="Optional — why does this policy exist?"
              onChange={(e) => onChange({ description: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
